import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';
import organisationSettingsService, { OrganisationSettings } from '../services/organisationSettingsService';
import { parseExecutiveChatResponse, parseRecommendedQuestions, stripRecommendedQuestions, extractFinalReportContent, hasExportableTabularData } from '../utils/executiveChatParser';
import { exportExecutiveChatToWord } from '../components/executive/ExecutiveChatExport';
import KPICards from '../components/executive/KPICards';
import LineChartCard from '../components/executive/LineChartCard';
import BarChartCard from '../components/executive/BarChartCard';
import HighlightBox from '../components/executive/HighlightBox';
import MarkdownText from '../components/executive/MarkdownText';
import MapVisualization from '../components/visualizations/MapVisualization';
import RentalDashboard from '../components/executive/RentalDashboard';
import MicrodebRevenueDashboard from '../components/executive/MicrodebRevenueDashboard';
import SaveChatModal from '../components/executive/SaveChatModal';
import ChatHistoryPanel from '../components/executive/ChatHistoryPanel';
import ThemeToggle from '../components/shared/ThemeToggle';
import { chatHistoryService, ChatHistoryItem } from '../services/chatHistoryService';
import luminaLogoDark from '../assets/LuminaAilogoPNG-dark.png';
import './ExecutiveChatPage.css';

interface MessageEvent {
  type: 'text' | 'tool_use' | 'tool_result';
  content?: string;
  tool?: string;
  timestamp: number;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string; // Full content for backwards compatibility
  timestamp: Date;
  isStreaming?: boolean;
  isWaitingForResponse?: boolean;
  events?: MessageEvent[]; // Timeline of events
  toolCalls?: Array<{ tool: string; status: 'pending' | 'complete' }>; // Deprecated, kept for compatibility
  recommendedQuestions?: string[]; // AI-suggested follow-up questions
  tokens?: number; // Content-based token count for this message (EXCLUDES the fixed system prompt)
  isSummary?: boolean; // Flag to indicate this is a summarized message
}

const CONTEXT_TOKEN_LIMIT = 100000;
const SYSTEM_PROMPT_BASELINE_TOKENS = 50000;

const CONVERSATION_TOKEN_BUDGET = CONTEXT_TOKEN_LIMIT - SYSTEM_PROMPT_BASELINE_TOKENS;

const ExecutiveChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { theme } = useTheme();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  const [organizationSettings, setOrganizationSettings] = useState<OrganisationSettings | null>(null);
  const [whitelabelPrimaryLogo, setWhitelabelPrimaryLogo] = useState<string | null>(null);
  const [whitelabelSecondaryLogo, setWhitelabelSecondaryLogo] = useState<string | null>(null);
  const [exportingMessageId, setExportingMessageId] = useState<string | null>(null);
  const [exportingPowerPointMessageId, setExportingPowerPointMessageId] = useState<string | null>(null);
  const [exportingExcelMessageId, setExportingExcelMessageId] = useState<string | null>(null);
  const [exportingPDFMessageId, setExportingPDFMessageId] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<string>('');
  const [conversationTokens, setConversationTokens] = useState<number>(0);
  const [contextTokens, setContextTokens] = useState<number>(0); // Total tokens in conversation context
  const [useExplicitContextTokens, setUseExplicitContextTokens] = useState<boolean>(false); // Flag to preserve loaded token count
  const [collapsedThinking, setCollapsedThinking] = useState<Record<string, boolean>>({});
  const [availablePersonas, setAvailablePersonas] = useState<any[]>([]);
  const [allPersonas, setAllPersonas] = useState<any[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(null);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [iconCacheBuster] = useState<number>(Date.now()); // Generate once on mount
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  // Chat History State
  const [showSaveChatModal, setShowSaveChatModal] = useState(false);
  const [showChatHistoryPanel, setShowChatHistoryPanel] = useState(false);
  const [savedChats, setSavedChats] = useState<ChatHistoryItem[]>([]);
  const [savedChatsCollapsed, setSavedChatsCollapsed] = useState(false);
  const [chatCount, setChatCount] = useState(0);
  const [loadingChats, setLoadingChats] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [hoveredChat, setHoveredChat] = useState<{ id: number; summary: string; top: number; left: number } | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverTargetRef = useRef<HTMLDivElement | null>(null);
  const sidebarResizing = useRef(false);

  // Track which messages have had their initial collapse state set
  const initializedCollapseRef = useRef<Set<string>>(new Set());
  
  // Rotating placeholder text
  const placeholderTexts = [
    "Which KPIs have the strongest correlation with overall profitability across all locations?",
    "What operational factors separate high performers from low performers?",
    "Show me the complete commercial picture for my top 5 locations"
  ];
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  
  // Refs
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null);
  const voiceStopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const voiceTranscriptRef = useRef<string>('');

  // Load organization logo and settings
  const loadOrganizationLogo = async () => {
    if (!user) return;
    
    try {
      const settings = await organisationSettingsService.getOrganisationSettings();
      setOrganizationSettings(settings);
      
      if (settings.logoUrl) {
        // logoUrl now comes from database endpoint like /api/organisation-settings/logo/{orgId}
        const baseUrl = settings.logoUrl.startsWith('http') 
          ? settings.logoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.logoUrl}`;
        const logoUrl = `${baseUrl}?t=${Date.now()}`;
        setOrganizationLogo(logoUrl);
      } else {
        setOrganizationLogo(null);
      }
      
      // Load whitelabel logos
      if (settings.whitelabelPrimaryLogoUrl) {
        const baseUrl = settings.whitelabelPrimaryLogoUrl.startsWith('http') 
          ? settings.whitelabelPrimaryLogoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.whitelabelPrimaryLogoUrl}`;
        setWhitelabelPrimaryLogo(`${baseUrl}?t=${Date.now()}`);
      } else {
        setWhitelabelPrimaryLogo(null);
      }
      
      if (settings.whitelabelSecondaryLogoUrl) {
        const baseUrl = settings.whitelabelSecondaryLogoUrl.startsWith('http') 
          ? settings.whitelabelSecondaryLogoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.whitelabelSecondaryLogoUrl}`;
        setWhitelabelSecondaryLogo(`${baseUrl}?t=${Date.now()}`);
      } else {
        setWhitelabelSecondaryLogo(null);
      }
    } catch (error) {
      console.error('Error loading organization logo:', error);
      setOrganizationLogo(null);
      setOrganizationSettings(null);
      setWhitelabelPrimaryLogo(null);
      setWhitelabelSecondaryLogo(null);
    }
  };

  // Load chat count on mount
  const loadChatCount = async () => {
    try {
      console.log('📊 Loading chat count...');
      const countData = await chatHistoryService.getChatCount();
      setChatCount(countData.count);
      console.log(`✅ Chat count loaded: ${countData.count}/10`);
    } catch (error) {
      console.error('❌ Failed to load chat count:', error);
    }
  };

  // Load organization logo and personas on mount
  useEffect(() => {
    if (user) {
      loadOrganizationLogo();
      fetchAvailablePersonas();
      fetchAllPersonas();
      loadChatCount(); // Load chat count on mount
      loadSavedChats(); // Load saved chats for sidebar
    }
    
    // Listen for whitelabel settings updates
    const handleWhitelabelUpdate = () => {
      loadOrganizationLogo();
    };
    
    window.addEventListener('whitelabelSettingsUpdated', handleWhitelabelUpdate);
    
    return () => {
      window.removeEventListener('whitelabelSettingsUpdated', handleWhitelabelUpdate);
    };
  }, [user]);

  // Fetch available personas for this user's organization
  const fetchAvailablePersonas = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/available`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch personas');
      }

      const data = await response.json();
      
      if (data.success && data.data.length > 0) {
        setAvailablePersonas(data.data);
        // Auto-select the default persona or first persona
        const defaultPersona = data.data.find((p: any) => p.is_default);
        const selectedPersona = defaultPersona || data.data[0];
        setSelectedPersonaId(selectedPersona.id);
      }
    } catch (err: any) {
      console.error('Error fetching personas:', err);
    }
  };

  // Fetch ALL personas (to show in modal)
  const fetchAllPersonas = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/all-with-access`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all personas');
      }

      const data = await response.json();
      
      if (data.success) {
        setAllPersonas(data.data);
        // If no persona selected yet, auto-select the default or first available
        setSelectedPersonaId(prev => {
          if (prev !== null) return prev;
          const available = data.data.filter((p: any) => p.is_available && p.visibility !== 'hidden' && p.visibility !== 'inactive');
          const defaultP = available.find((p: any) => p.is_default);
          return (defaultP || available[0])?.id ?? null;
        });
      }
    } catch (err: any) {
      console.error('Error fetching all personas:', err);
    }
  };

  // Force parent containers to allow scrolling
  useEffect(() => {
    const root = document.getElementById('root');
    const app = document.querySelector('.App') as HTMLElement;
    const body = document.body;
    const html = document.documentElement;

    // Store original styles
    const originalStyles = {
      root: root ? {
        overflow: root.style.overflow,
        height: root.style.height,
        minHeight: root.style.minHeight,
        maxHeight: root.style.maxHeight
      } : null,
      app: app ? {
        overflow: app.style.overflow,
        height: app.style.height,
        minHeight: app.style.minHeight,
        maxHeight: app.style.maxHeight,
        display: app.style.display
      } : null,
      body: {
        overflow: body.style.overflow,
        height: body.style.height,
        minHeight: body.style.minHeight,
        maxHeight: body.style.maxHeight
      },
      html: {
        overflow: html.style.overflow,
        height: html.style.height,
        minHeight: html.style.minHeight,
        maxHeight: html.style.maxHeight
      }
    };

    // Apply scroll-friendly styles
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
      root.style.minHeight = '100vh';
      root.style.maxHeight = 'none';
    }
    if (app) {
      app.style.overflow = 'visible';
      app.style.height = 'auto';
      app.style.minHeight = '100vh';
      app.style.maxHeight = 'none';
      app.style.display = 'block';
    }
    body.style.overflowY = 'auto';
    body.style.overflowX = 'hidden';
    body.style.height = 'auto';
    body.style.minHeight = '100vh';
    body.style.maxHeight = 'none';

    html.style.overflowY = 'auto';
    html.style.overflowX = 'hidden';
    html.style.height = 'auto';
    html.style.minHeight = '100vh';
    html.style.maxHeight = 'none';

    console.log('📜 Executive Chat: Scroll styles applied');

    // Cleanup on unmount
    return () => {
      if (root && originalStyles.root) {
        root.style.overflow = originalStyles.root.overflow;
        root.style.height = originalStyles.root.height;
        root.style.minHeight = originalStyles.root.minHeight;
        root.style.maxHeight = originalStyles.root.maxHeight;
      }
      if (app && originalStyles.app) {
        app.style.overflow = originalStyles.app.overflow;
        app.style.height = originalStyles.app.height;
        app.style.minHeight = originalStyles.app.minHeight;
        app.style.maxHeight = originalStyles.app.maxHeight;
        app.style.display = originalStyles.app.display;
      }
      body.style.overflow = originalStyles.body.overflow;
      body.style.height = originalStyles.body.height;
      body.style.minHeight = originalStyles.body.minHeight;
      body.style.maxHeight = originalStyles.body.maxHeight;

      html.style.overflow = originalStyles.html.overflow;
      html.style.height = originalStyles.html.height;
      html.style.minHeight = originalStyles.html.minHeight;
      html.style.maxHeight = originalStyles.html.maxHeight;

      console.log('📜 Executive Chat: Original scroll styles restored');
    };
  }, []);

  // Scroll to bottom - disabled to allow manual scrolling
  // const scrollToBottom = () => {
  //   messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  // };

  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages]);

  // Rotate placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Generate message ID
  const generateId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Handle Word export
  const handleExportToWord = async (message: Message) => {
    if (message.role !== 'assistant' || !message.content) {
      console.error('Cannot export: invalid message');
      return;
    }
    
    // Find the user message that prompted this response
    const messageIndex = messages.findIndex(m => m.id === message.id);
    let userQuestion = 'Executive Analysis';
    
    if (messageIndex > 0) {
      // Find the most recent user message before this assistant message
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userQuestion = messages[i].content;
          break;
        }
      }
    }
    
    try {
      setExportingMessageId(message.id);
      
      // Find the DOM element for this message
      const messageElement = document.querySelector(`[data-message-id="${message.id}"]`) as HTMLElement;
      
      if (!messageElement) {
        throw new Error('Message element not found in DOM');
      }
      
      await exportExecutiveChatToWord({
        messageContent: extractFinalReportContent(message),
        userQuestion: userQuestion,
        messageElement: messageElement,
        organizationSettings: organizationSettings
      });
      
      console.log('✅ Export successful');
    } catch (error) {
      console.error('❌ Export failed:', error);
      setError('Failed to export to Word. Please try again.');
    } finally {
      setExportingMessageId(null);
    }
  };

  // Handle PowerPoint export
  const handleExportToPowerPoint = async (message: Message) => {
    if (message.role !== 'assistant' || !message.content) {
      console.error('Cannot export: invalid message');
      return;
    }
    
    // Find the user question that prompted this response
    const messageIndex = messages.findIndex(m => m.id === message.id);
    let userQuestion = 'Executive Analysis';
    
    if (messageIndex > 0) {
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userQuestion = messages[i].content;
          break;
        }
      }
    }
    
    try {
      setExportingPowerPointMessageId(message.id);
      setExportProgress('Preparing export...');
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      
      console.log('📊 Requesting PowerPoint export...');
      setExportProgress('AI is reformatting for slides...');
      
      const response = await fetch(`${API_URL}/executive-chat/export-powerpoint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          messageContent: extractFinalReportContent(message),
          userQuestion: userQuestion,
          messageId: message.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(errorData.message || 'Export failed');
      }
      
      setExportProgress('Generating PowerPoint file...');
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `Executive_Analysis_${Date.now()}.pptx`;
      
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setExportProgress('Download complete!');
      setTimeout(() => {
        setExportingPowerPointMessageId(null);
        setExportProgress('');
      }, 2000);
      
      console.log('✅ PowerPoint export successful');
      
    } catch (error: any) {
      console.error('❌ PowerPoint export failed:', error);
      setError(`Failed to export to PowerPoint: ${error.message}`);
      setExportingPowerPointMessageId(null);
      setExportProgress('');
    }
  };

  // Handle Excel export
  const handleExportToExcel = async (message: Message) => {
    if (message.role !== 'assistant' || !message.content) {
      console.error('Cannot export: invalid message');
      return;
    }
    
    // Find the user question that prompted this response
    const messageIndex = messages.findIndex(m => m.id === message.id);
    let userQuestion = 'Executive Analysis';
    
    if (messageIndex > 0) {
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userQuestion = messages[i].content;
          break;
        }
      }
    }
    
    try {
      setExportingExcelMessageId(message.id);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      
      console.log('📊 Requesting Excel export...');
      
      const response = await fetch(`${API_URL}/executive-chat/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          messageContent: extractFinalReportContent(message),
          userQuestion: userQuestion,
          messageId: message.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(errorData.message || 'Export failed');
      }
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `Executive_Tables_${Date.now()}.xlsx`;
      
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setExportingExcelMessageId(null);
      }, 1000);
      
      console.log('✅ Excel export successful');
      
    } catch (error: any) {
      console.error('❌ Excel export failed:', error);
      setError(`Failed to export to Excel: ${error.message}`);
      setExportingExcelMessageId(null);
    }
  };

  // Handle PDF export
  const handleExportToPDF = async (message: Message) => {
    if (message.role !== 'assistant' || !message.content) {
      console.error('Cannot export: invalid message');
      return;
    }
    
    // Find the user question that prompted this response
    const messageIndex = messages.findIndex(m => m.id === message.id);
    let userQuestion = 'Executive Analysis';
    
    if (messageIndex > 0) {
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          userQuestion = messages[i].content;
          break;
        }
      }
    }
    
    try {
      setExportingPDFMessageId(message.id);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      
      console.log('📄 Requesting PDF export...');
      
      const response = await fetch(`${API_URL}/executive-chat/export-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          messageContent: extractFinalReportContent(message),
          userQuestion: userQuestion,
          messageId: message.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(errorData.message || 'Export failed');
      }
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `Executive_Report_${Date.now()}.pdf`;
      
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setExportingPDFMessageId(null);
      }, 1000);
      
      console.log('✅ PDF export successful');
      
    } catch (error: any) {
      console.error('❌ PDF export failed:', error);
      setError(`Failed to export to PDF: ${error.message}`);
      setExportingPDFMessageId(null);
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    if (messages.length === 0) {
      // No messages, nothing to clear
      return;
    }
    
    // Confirm if there are messages
    const confirmClear = window.confirm('Start a new conversation? This will clear the current chat history.');
    if (!confirmClear) {
      return;
    }
    
    // Clear messages and reset state
    setMessages([]);
    setInputMessage('');
    setError(null);
    setConversationTokens(0);
    setContextTokens(0);
    setUseExplicitContextTokens(false); // Use dynamic calculation for new chat
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    console.log('🔄 Started new conversation');
  };

  // ============================================================================
  // Chat History Functions
  // ============================================================================

  // Load saved chats list
  const loadSavedChats = async () => {
    try {
      setLoadingChats(true);
      const chats = await chatHistoryService.getUserChats();
      setSavedChats(chats);
      setChatCount(chats.length);
      console.log(`📚 Loaded ${chats.length} saved chats`);
    } catch (error: any) {
      console.error('Failed to load saved chats:', error);
      setError('Failed to load saved chats');
    } finally {
      setLoadingChats(false);
    }
  };

  // Generate auto title from first user message
  const generateAutoTitle = (): string => {
    const firstUserMessage = messages.find(m => m.role === 'user');
    if (!firstUserMessage) return 'Untitled Chat';
    
    let title = firstUserMessage.content.substring(0, 50);
    if (firstUserMessage.content.length > 50) {
      title = title.substring(0, title.lastIndexOf(' ')) + '...';
    }
    return title || 'Untitled Chat';
  };

  // Open save chat modal
  const handleOpenSaveChatModal = async () => {
    if (messages.length === 0) {
      setError('No messages to save');
      return;
    }
    
    // Load current chat count
    try {
      const countData = await chatHistoryService.getChatCount();
      setChatCount(countData.count);
    } catch (error) {
      console.error('Failed to get chat count:', error);
    }
    
    setShowSaveChatModal(true);
  };

  // Handle save chat
  const handleSaveChat = async (title: string) => {
    try {
      console.log(`💾 Saving chat: "${title}"`);
      
      await chatHistoryService.saveChat({
        chatTitle: title,
        messages: messages,
        personaId: selectedPersonaId,
        personaName: availablePersonas.find(p => p.id === selectedPersonaId)?.name || null,
        totalTokens: conversationTokens
      });
      
      console.log('✅ Chat saved successfully');
      setShowSaveChatModal(false);
      setError(null);
      
      // Show success message
      const successToast = document.createElement('div');
      successToast.className = 'chat-save-toast';
      successToast.textContent = '📚 Chat saved successfully!';
      successToast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4caf50;
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        z-index: 10001;
        font-weight: 500;
        animation: slideIn 0.3s ease-out;
      `;
      document.body.appendChild(successToast);
      setTimeout(() => {
        successToast.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => document.body.removeChild(successToast), 300);
      }, 3000);
      
      // Reload sidebar chat list and count
      await loadSavedChats();
      await loadChatCount();
      
    } catch (error: any) {
      console.error('Failed to save chat:', error);
      throw error; // Re-throw so modal can show the error
    }
  };

  // Handle load chat
  const handleLoadChat = async (chatId: number) => {
    try {
      // Confirm if there are unsaved messages
      if (messages.length > 0) {
        const confirm = window.confirm(
          'Loading a saved chat will replace your current conversation. Continue?'
        );
        if (!confirm) return;
      }
      
      console.log(`📖 Loading chat ${chatId}`);
      setLoadingChats(true);
      
      const chatData = await chatHistoryService.loadChat(chatId);
      
      // Load messages into current conversation
      const loadedMessages = chatData.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
      
      setMessages(loadedMessages);
      
      // Use the actual total tokens from database and preserve it. This is a count of tokens the
      // conversation actually consumed, so it is already on the same (baseline-free) scale as
      // calculateContextTokens() and can be assigned to contextTokens directly.
      const actualTokens = chatData.totalTokensUsed || 0;
      setContextTokens(actualTokens);
      setConversationTokens(actualTokens);
      setUseExplicitContextTokens(true); // Preserve this value, only add new response tokens

      console.log(`📊 Loaded chat: ${actualTokens.toLocaleString()} conversation tokens (${Math.round((actualTokens / CONVERSATION_TOKEN_BUDGET) * 100)}%)`);
      
      // Update persona if different
      if (chatData.personaId && chatData.personaId !== selectedPersonaId) {
        setSelectedPersonaId(chatData.personaId);
      }
      
      setShowChatHistoryPanel(false);
      setError(null);
      
      console.log(`✅ Chat loaded: "${chatData.chatTitle}" (${chatData.messageCount} messages)`);
      
      // Show success message
      const successToast = document.createElement('div');
      successToast.textContent = `📂 Loaded "${chatData.chatTitle}"`;
      successToast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #2196f3;
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        z-index: 10001;
        font-weight: 500;
      `;
      document.body.appendChild(successToast);
      setTimeout(() => document.body.removeChild(successToast), 3000);
      
    } catch (error: any) {
      console.error('Failed to load chat:', error);
      setError(`Failed to load chat: ${error.message}`);
    } finally {
      setLoadingChats(false);
    }
  };

  // Handle delete chat
  const handleDeleteChat = async (chatId: number) => {
    try {
      console.log(`🗑️ Deleting chat ${chatId}`);
      setHoveredChat(null);
      await chatHistoryService.deleteChat(chatId);
      console.log('✅ Chat deleted');

      // Reload chat list
      await loadSavedChats();
      
    } catch (error: any) {
      console.error('Failed to delete chat:', error);
      setError(`Failed to delete chat: ${error.message}`);
    }
  };

  // Handle rename chat
  const handleRenameChat = async (chatId: number, newTitle: string) => {
    try {
      console.log(`✏️ Renaming chat ${chatId} to "${newTitle}"`);
      await chatHistoryService.updateChatTitle(chatId, newTitle);
      console.log('✅ Chat renamed');
      
      // Reload chat list
      await loadSavedChats();
      
    } catch (error: any) {
      console.error('Failed to rename chat:', error);
      setError(`Failed to rename chat: ${error.message}`);
      throw error;
    }
  };

  const handleChatMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>, chat: ChatHistoryItem) => {
    if (!chat.chatSummary) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    const el = e.currentTarget;
    hoverTargetRef.current = el;

    hoverTimeoutRef.current = setTimeout(() => {
      hoverTimeoutRef.current = null;
      if (hoverTargetRef.current !== el || !el.matches(':hover')) return;
      const rect = el.getBoundingClientRect();
      setHoveredChat({
        id: chat.id,
        summary: chat.chatSummary,
        top: rect.top,
        left: rect.right + 8,
      });
    }, 400);
  }, []);

  const handleChatMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    hoverTargetRef.current = null;
    setHoveredChat(null);
  }, []);

  useEffect(() => {
    const dismiss = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      hoverTargetRef.current = null;
      setHoveredChat(prev => (prev ? null : prev));
    };

    window.addEventListener('scroll', dismiss, true); // capture: catches scroll in the sidebar too
    document.addEventListener('mouseleave', dismiss);

    return () => {
      window.removeEventListener('scroll', dismiss, true);
      document.removeEventListener('mouseleave', dismiss);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleSidebarResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    sidebarResizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMouseMove = (ev: MouseEvent) => {
      if (!sidebarResizing.current) return;
      const newWidth = Math.min(480, Math.max(200, startWidth + (ev.clientX - startX)));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      sidebarResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  // Handle open chat history panel
  const handleOpenChatHistory = async () => {
    await loadSavedChats();
    setShowChatHistoryPanel(true);
  };

  // Render timeline of events (text + tool calls inline)
  const renderTimeline = (events: MessageEvent[], messageId: string) => {
    // Find the index where final response starts (after last tool_result)
    const lastToolResultIndex = events.map((e, i) => e.type === 'tool_result' ? i : -1).filter(i => i !== -1).pop() ?? -1;
    
    // Separate thinking events (everything before and including last tool result) from final response
    const thinkingEvents = lastToolResultIndex >= 0 ? events.slice(0, lastToolResultIndex + 1) : [];
    const finalResponseEvents = lastToolResultIndex >= 0 ? events.slice(lastToolResultIndex + 1) : events;
    
    // Initialize collapse state only once when final response appears
    const hasFinalResponse = finalResponseEvents.length > 0 && finalResponseEvents.some(e => e.type === 'text');
    if (hasFinalResponse && !initializedCollapseRef.current.has(messageId)) {
      initializedCollapseRef.current.add(messageId);
      if (collapsedThinking[messageId] === undefined) {
        setCollapsedThinking(prev => ({
          ...prev,
          [messageId]: true
        }));
      }
    }
    
    // Use the stored state, defaulting to false (expanded) if not set yet
    const isThinkingCollapsed = collapsedThinking[messageId] ?? false;
    
    const toggleThinking = () => {
      setCollapsedThinking(prev => ({
        ...prev,
        [messageId]: !isThinkingCollapsed
      }));
    };
    
    return (
      <div className="ec-msg-timeline">
        {thinkingEvents.length > 0 && (
          <div className="ec-thinking-section">
            <button 
              className="ec-thinking-toggle"
              onClick={toggleThinking}
            >
              <span className="ec-toggle-icon">{isThinkingCollapsed ? '▶' : '▼'}</span>
              <span className="ec-thinking-label">💭 Lumina AI's thoughts</span>
              <span className="ec-thinking-count">({thinkingEvents.filter(e => e.type === 'tool_use' || e.type === 'tool_result').length} steps)</span>
            </button>
            {!isThinkingCollapsed && (
              <div className="ec-thinking-content">
                {thinkingEvents.map((event, idx) => {
                  if (event.type === 'text') {
                    // Render thinking text
                    return (
                      <div key={`thinking-text-${idx}`} className="ec-timeline-thinking-text">
                        <div className="ec-thinking-text-icon">💭</div>
                        <div className="ec-thinking-text-content">
                          <MarkdownText content={event.content || ''} />
                        </div>
                      </div>
                    );
                  } else if (event.type === 'tool_use') {
                    return (
                      <div key={`tool-use-${idx}`} className="ec-timeline-tool-use">
                        <div className="ec-tool-icon">🔧</div>
                        <div className="ec-tool-label">Querying database: <strong>{event.tool}</strong></div>
                      </div>
                    );
                  } else if (event.type === 'tool_result') {
                    return (
                      <div key={`tool-result-${idx}`} className="ec-timeline-tool-result">
                        <div className="ec-tool-icon">✅</div>
                        <div className="ec-tool-label">Data retrieved, analyzing...</div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
        )}
        
        {finalResponseEvents.map((event, idx) => {
          if (event.type === 'text') {
            // Parse and render final response text content
            const blocks = parseExecutiveChatResponse(event.content || '');
            return (
              <div key={`text-${idx}`} className="ec-timeline-text-block">
                {blocks.map((block) => {
                  switch (block.type) {
                    case 'kpi-cards':
                      return <KPICards key={block.id} data={block.content} />;
                    case 'line-chart':
                      return <LineChartCard key={block.id} config={block.content} />;
                    case 'bar-chart':
                      return <BarChartCard key={block.id} config={block.content} />;
                    case 'highlight':
                      return <HighlightBox key={block.id} config={block.content} />;
                    case 'map':
                      // Render map visualization in timeline
                      if (block.content && Array.isArray(block.content.data)) {
                        return (
                          <MapVisualization
                            key={block.id}
                            title={block.content.title}
                            data={block.content.data}
                            mapType={block.content.mapType || 'markers'}
                            colorScale={block.content.colorScale || 'performance'}
                            valueLabel={block.content.valueLabel || 'Value'}
                            markerSize={block.content.markerSize || 'scaled'}
                          />
                        );
                      }
                      return null;
                    case 'text':
                    default:
                      // Ensure content is a string
                      const textContent = typeof block.content === 'string' 
                        ? block.content 
                        : JSON.stringify(block.content, null, 2);
                      return <MarkdownText key={block.id} content={textContent} />;
                  }
                })}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  // Custom renderer using structured content blocks (fallback for old messages)
  const renderAIContent = (content: string) => {
    // Parse AI response into structured blocks
    const blocks = parseExecutiveChatResponse(content);

    // DEBUG: Log blocks being rendered
    const hasMap = blocks.some(b => b.type === 'map');
    if (hasMap) {
      console.log('🎨 [RENDER DEBUG] Rendering blocks:', blocks.map(b => ({
        id: b.id,
        type: b.type,
        contentType: typeof b.content
      })));
    }

    // Render each block with appropriate component
    return (
      <>
        {blocks.map((block) => {
          // DEBUG: Log each block as it's rendered
          if (hasMap) {
            console.log(`🎨 [RENDER DEBUG] Rendering block ${block.id} of type "${block.type}", content type: ${typeof block.content}`);
          }
          
          switch (block.type) {
            case 'kpi-cards':
              return <KPICards key={block.id} data={block.content} />;
            
            case 'line-chart':
              return <LineChartCard key={block.id} config={block.content} />;
            
            case 'bar-chart':
              return <BarChartCard key={block.id} config={block.content} />;
            
            case 'highlight':
              return <HighlightBox key={block.id} config={block.content} />;
            
            case 'map':
              console.log('🗺️ [RENDER DEBUG] Map case hit! Content:', {
                hasContent: !!block.content,
                hasData: !!block.content?.data,
                isDataArray: Array.isArray(block.content?.data),
                dataLength: block.content?.data?.length
              });
              // Ensure we have valid map data before rendering
              if (block.content && Array.isArray(block.content.data)) {
                console.log('🗺️ [RENDER DEBUG] Rendering MapVisualization with data:', block.content.data);
                return (
                  <MapVisualization
                    key={block.id}
                    title={block.content.title}
                    data={block.content.data}
                    mapType={block.content.mapType || 'markers'}
                    colorScale={block.content.colorScale || 'performance'}
                    valueLabel={block.content.valueLabel || 'Value'}
                    markerSize={block.content.markerSize || 'scaled'}
                  />
                );
              }
              // If map data is invalid, skip rendering
              console.warn('🗺️ [RENDER DEBUG] Invalid map data, returning null:', block.content);
              return null;
            
            case 'text':
            default:
              // DEBUG: Log when hitting default case
              if (block.type !== 'text') {
                console.warn(`🎨 [RENDER DEBUG] Unknown block type "${block.type}" falling to default, content type: ${typeof block.content}`);
              }
              // Ensure content is a string before passing to MarkdownText
              const textContent = typeof block.content === 'string' 
                ? block.content 
                : JSON.stringify(block.content, null, 2);
              if (typeof block.content !== 'string') {
                console.warn('🎨 [RENDER DEBUG] Non-string content converted to JSON:', textContent.substring(0, 200));
              }
              return <MarkdownText key={block.id} content={textContent} />;
          }
        })}
      </>
    );
  };

  const calculateContextTokens = (msgs: Message[]): number => {
    return msgs
      .filter(m => !m.isStreaming) // Don't include incomplete messages
      .reduce((total, msg) => {
        // Use stored content-token count, or estimate from content if not available
        const msgTokens = msg.tokens || Math.ceil(msg.content.length / 4);
        return total + msgTokens;
      }, 0);
  };

  // Build conversation history
  const buildConversationHistory = (msgs: Message[]) => {
    return msgs
      .filter(m => !m.isStreaming) // Don't include incomplete messages
      .filter(m => m.content && m.content.trim() !== '') // Don't include empty messages
      .map(m => {
        // Summary messages are already condensed, include them as-is
        // Regular messages are included in full
        return {
          role: m.role,
          content: m.content,
          ...(m.isSummary && { isSummary: true }) // Flag summary messages for backend awareness
        };
      });
  };

  // Handle file attachment
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    
    // Validate file types - CSV and PDF
    const validFiles = newFiles.filter(file => {
      // Check file type - CSV or PDF
      const isCSV = file.type === 'text/csv' || 
                    file.type === 'application/csv' ||
                    file.type === 'text/plain' ||
                    file.name.toLowerCase().endsWith('.csv');
      
      const isPDF = file.type === 'application/pdf' ||
                    file.name.toLowerCase().endsWith('.pdf');

      if (!isCSV && !isPDF) {
        setError(`File ${file.name} is not supported. Please upload CSV or PDF files only.`);
        return false;
      }

      // Check file size (10MB limit per file)
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Maximum size is 10MB per file.`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles]);
      setError(null);
    }

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  // Voice recording - click to start, stops after 2 seconds of silence, captures text to input field

  const startVoiceRecognition = () => {
    // Check for browser support
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setError('Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-GB'; // British English

      // Reset stop timer on activity
      const resetStopTimer = () => {
        if (voiceStopTimeoutRef.current) {
          clearTimeout(voiceStopTimeoutRef.current);
        }
        // Stop listening after 2 seconds of silence
        voiceStopTimeoutRef.current = setTimeout(() => {
          if (recognition) {
            recognition.stop();
          }
        }, 2000);
      };

      recognition.onstart = () => {
        setIsRecording(true);
        setError(null);
        // Don't clear voiceTranscriptRef - allow appending to existing text
      };

      recognition.onresult = (event: any) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            // Update input with final transcript
            setInputMessage(prev => {
              const trimmedPrev = prev.trim();
              const newValue = trimmedPrev ? trimmedPrev + ' ' + transcript : transcript;
              voiceTranscriptRef.current = newValue;
              return newValue;
            });
            // Reset timer after final result
            resetStopTimer();
          } else {
            // Reset timer on interim results too (user is still speaking)
            resetStopTimer();
          }
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        if (voiceStopTimeoutRef.current) {
          clearTimeout(voiceStopTimeoutRef.current);
          voiceStopTimeoutRef.current = null;
        }

        switch (event.error) {
          case 'not-allowed':
            setError('Microphone access denied. Please allow microphone access and try again.');
            break;
          case 'no-speech':
            // No speech detected - will stop naturally
            break;
          case 'audio-capture':
            setError('No microphone found. Please connect a microphone and try again.');
            break;
          case 'network':
            setError('Network error during speech recognition. Please check your connection.');
            break;
          default:
            setError(`Speech recognition error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        setIsRecording(false);
        speechRecognitionRef.current = null;
        if (voiceStopTimeoutRef.current) {
          clearTimeout(voiceStopTimeoutRef.current);
          voiceStopTimeoutRef.current = null;
        }
        // Text is already in input field, user can review and send manually
      };

      recognition.start();
      speechRecognitionRef.current = recognition;
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setError('Failed to start voice input. Please try again.');
    }
  };

  const toggleVoiceRecording = () => {
    if (isRecording) {
      // Stop current recording
      if (voiceStopTimeoutRef.current) {
        clearTimeout(voiceStopTimeoutRef.current);
        voiceStopTimeoutRef.current = null;
      }
      speechRecognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      // Start recording (appends to existing text in input field)
      startVoiceRecognition();
    }
  };

  // Voice mode no longer auto-restarts - user manually clicks to record again

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
      if (voiceStopTimeoutRef.current) {
        clearTimeout(voiceStopTimeoutRef.current);
      }
    };
  }, []);

  // Calculate context tokens whenever messages change (unless preserving loaded value)
  useEffect(() => {
    if (!useExplicitContextTokens) {
      const totalContextTokens = calculateContextTokens(messages);
      setContextTokens(totalContextTokens);
    }
  }, [messages, useExplicitContextTokens]);

  // Auto-summarize conversation when context exceeds the token limit.
  useEffect(() => {
    const handleAutoSummarize = async () => {
      const olderMessages = messages.slice(0, -2);
      const nothingNewToCompress =
        olderMessages.length <= 1 && olderMessages.every(m => m.isSummary);

      const modelContextTokens = SYSTEM_PROMPT_BASELINE_TOKENS + contextTokens;

      // Only summarize if:
      // 1. Real model-side context over the limit
      // 2. Not currently streaming
      // 3. Have at least 3 messages
      // 4. There is genuinely new content to roll into a summary
      if (
        modelContextTokens > CONTEXT_TOKEN_LIMIT &&
        !isStreaming &&
        messages.length >= 3 &&
        !nothingNewToCompress
      ) {
        console.log(`⚠️ Context size (${modelContextTokens.toLocaleString()} tokens incl. system prompt) exceeded ${CONTEXT_TOKEN_LIMIT.toLocaleString()} threshold`);
        console.log('📝 Auto-summarizing conversation...');
        
        try {
          const summarizedMessages = await chatHistoryService.summarizeConversation(messages);
          
          // Convert timestamps to Date objects for component state
          const normalizedMessages: Message[] = summarizedMessages.map(msg => ({
            ...msg,
            timestamp: typeof msg.timestamp === 'string' ? new Date(msg.timestamp) : msg.timestamp
          }));
          
          setMessages(normalizedMessages);
          
          // Recalculate context tokens
          const newContextTokens = calculateContextTokens(normalizedMessages);
          setContextTokens(newContextTokens);
          
          console.log(`✅ Conversation summarized: ${messages.length} → ${normalizedMessages.length} messages`);
          console.log(`📊 Context reduced: ${contextTokens.toLocaleString()} → ${newContextTokens.toLocaleString()} tokens`);
          
        } catch (error) {
          console.error('❌ Auto-summarization failed:', error);
        }
      }
    };

    handleAutoSummarize();
  }, [contextTokens, isStreaming, messages]);

  // Upload files to backend and get file references
  const uploadAttachedFiles = async (): Promise<string[]> => {
    if (attachedFiles.length === 0) return [];

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    setUploadingFiles(true);
    const fileRefs: string[] = [];

    try {
      for (const file of attachedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bypassValidation', 'true'); // Allow non-CSV files

        const response = await fetch(`${API_BASE_URL}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const data = await response.json();
        fileRefs.push(data.fileRef);
      }

      return fileRefs;
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Failed to upload attachments. Please try again.');
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  // Handle send message with streaming
  const handleSendMessage = async (providedQuestion?: string) => {
    const question = providedQuestion || inputMessage.trim();
    
    if (!question || isStreaming) return;

    setInputMessage('');
    setError(null);
    adjustTextareaHeight(inputRef.current);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

    // Add user message
    // Estimate tokens for user message (rough approximation: 4 chars per token)
    const estimatedUserTokens = Math.ceil(question.length / 4);
    
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: question,
      timestamp: new Date(),
      tokens: estimatedUserTokens
    };
    
    setMessages(prev => [...prev, userMessage]);

    // Add assistant message (empty, will stream)
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isWaitingForResponse: true,
      events: [], // Initialize timeline of events
      toolCalls: [] // Keep for backwards compatibility
    };
    setMessages(prev => [...prev, assistantMessage]);

    setIsStreaming(true);

    try {
      // Upload attached files first (if any)
      let fileRefs: string[] = [];
      if (attachedFiles.length > 0) {
        try {
          fileRefs = await uploadAttachedFiles();
          // Clear attached files after successful upload
          setAttachedFiles([]);
        } catch (uploadError) {
          // Error already handled in uploadAttachedFiles
          setIsStreaming(false);
          // Remove the assistant message that was added
          setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
          return;
        }
      }

      // Create abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch(`${API_BASE_URL}/executive-chat/analyze-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          userQuestion: question,
          conversationHistory: buildConversationHistory(messages),
          personaId: selectedPersonaId, // Selected persona ID
          attachedFiles: fileRefs // Include file references
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;
          
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);

            if (event.type === 'persona') {
              // Persona loaded
              console.log('🎭 Persona loaded:', event.persona.name);
            } else if (event.type === 'chunk') {
              setMessages(prev => prev.map(msg => {
                if (msg.id === assistantMessageId) {
                  const newContent = msg.content + event.content;
                  const events = msg.events || [];
                  
                  // Add text event to timeline (or append to last text event if still streaming)
                  const lastEvent = events[events.length - 1];
                  const updatedEvents = lastEvent && lastEvent.type === 'text'
                    ? [...events.slice(0, -1), { ...lastEvent, content: (lastEvent.content || '') + event.content }]
                    : [...events, { type: 'text' as const, content: event.content, timestamp: Date.now() }];
                  
                  return { 
                    ...msg, 
                    content: newContent, 
                    events: updatedEvents,
                    isWaitingForResponse: false 
                  };
                }
                return msg;
              }));
            } else if (event.type === 'tool_use') {
              console.log('🔧 Tool use:', event.tool);
              setMessages(prev => prev.map(msg => {
                if (msg.id === assistantMessageId) {
                  const events = msg.events || [];
                  return {
                    ...msg,
                    events: [...events, { type: 'tool_use' as const, tool: event.tool, timestamp: Date.now() }],
                    toolCalls: [...(msg.toolCalls || []), { tool: event.tool, status: 'pending' as const }]
                  };
                }
                return msg;
              }));
            } else if (event.type === 'tool_result') {
              console.log('✅ Tool result received');
              setMessages(prev => prev.map(msg => {
                if (msg.id === assistantMessageId) {
                  const events = msg.events || [];
                  const updatedEvents = [...events, { type: 'tool_result' as const, timestamp: Date.now() }];
                  
                  // Update toolCalls status for backwards compatibility
                  const updatedToolCalls = msg.toolCalls && msg.toolCalls.length > 0
                    ? [...msg.toolCalls.slice(0, -1), { ...msg.toolCalls[msg.toolCalls.length - 1], status: 'complete' as const }]
                    : msg.toolCalls;
                  
                  return { ...msg, events: updatedEvents, toolCalls: updatedToolCalls };
                }
                return msg;
              }));
            } else if (event.type === 'complete') {
              // Log token usage if available
              let messageTokens = 0;
              if (event.usage) {
                const { inputTokens, outputTokens, totalTokens } = event.usage;
                messageTokens = outputTokens;
                const newTotal = conversationTokens + totalTokens;
                setConversationTokens(newTotal);

                if (useExplicitContextTokens) {
                  setContextTokens(prevContext => prevContext + outputTokens);
                }

                console.log('═══════════════════════════════════════════════════════');
                console.log('📊 TOKEN USAGE - This Response');
                console.log('═══════════════════════════════════════════════════════');
                console.log(`Input Tokens:  ${inputTokens.toLocaleString()}`);
                console.log(`Output Tokens: ${outputTokens.toLocaleString()}`);
                console.log(`Total Tokens:  ${totalTokens.toLocaleString()}`);
                console.log('');
                console.log('💬 CONVERSATION TOTAL');
                console.log(`Cumulative:    ${newTotal.toLocaleString()} tokens`);
                console.log('═══════════════════════════════════════════════════════');
              } else {
                console.log('✅ Streaming complete');
              }
              
              // Extract recommended questions and strip them from displayed content
              setMessages(prev => prev.map(msg => {
                if (msg.id === assistantMessageId) {
                  const questions = parseRecommendedQuestions(msg.content);
                  const cleanContent = stripRecommendedQuestions(msg.content);
                  console.log('💡 Extracted recommended questions:', questions);
                  console.log('🧹 Stripped questions from content');
                  
                  // Also strip from events timeline text content
                  const cleanedEvents = msg.events?.map(event => {
                    if (event.type === 'text' && event.content) {
                      return {
                        ...event,
                        content: stripRecommendedQuestions(event.content)
                      };
                    }
                    return event;
                  });
                  
                  return { 
                    ...msg, 
                    content: cleanContent, // Use cleaned content without the JSON block
                    events: cleanedEvents,
                    isStreaming: false, 
                    isWaitingForResponse: false,
                    recommendedQuestions: questions.length > 0 ? questions : undefined,
                    tokens: messageTokens // Store token count for this message
                  };
                }
                return msg;
              }));
              setIsStreaming(false);
            } else if (event.type === 'error') {
              console.error('❌ Streaming error:', event.error);
              setError(event.error);
              setMessages(prev => prev.map(msg => 
                msg.id === assistantMessageId
                  ? { ...msg, isStreaming: false, isWaitingForResponse: false, content: msg.content || 'An error occurred.' }
                  : msg
              ));
              setIsStreaming(false);
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError);
          }
        }
      }
    } catch (err: any) {
      console.error('Error in streaming:', err);
      if (err.name !== 'AbortError') {
        setError(err.message || 'An error occurred while processing your request.');
        setMessages(prev => prev.map(msg => 
          msg.id === assistantMessageId
            ? { ...msg, isStreaming: false, isWaitingForResponse: false }
            : msg
        ));
      }
      setIsStreaming(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(event.target.value);
    adjustTextareaHeight(event.target);
  };

  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputMessage]);


  return (
    <div className="executive-search-fullscreen" data-theme={theme}>
      {/* Sidebar */}
      <div className={`chat-sidebar ${sidebarOpen ? '' : 'collapsed'}`} style={sidebarOpen ? { width: sidebarWidth } : undefined}>
        {sidebarOpen && (
          <div className="sidebar-resize-handle" onMouseDown={handleSidebarResizeStart}>
            <div className="resize-grip" />
          </div>
        )}
        {sidebarOpen ? (
          <>
            <div className="sidebar-header">
              <span className="sidebar-brand-name">Lumina AI</span>
              <button className="sidebar-panel-toggle" onClick={() => setSidebarOpen(false)} title="Close sidebar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              </button>
            </div>

            <button className="new-chat-button" onClick={handleNewConversation}>
              + New Chat
            </button>

            {messages.length > 0 && (
              <button
                className="sidebar-save-chat-button"
                onClick={handleOpenSaveChatModal}
                disabled={isStreaming}
                title="Save this conversation"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>Save Chat</span>
              </button>
            )}

            <div className="sidebar-groups">
              {/* Saved Chats Group */}
              <div className="sidebar-group">
                <div className="sidebar-group-header" onClick={() => setSavedChatsCollapsed(prev => !prev)} style={{ cursor: 'pointer' }}>
                  <div className="sidebar-group-label">
                    <span className="sidebar-group-toggle">{savedChatsCollapsed ? '▶' : '▼'}</span>
                    <span>💾 Saved Chats</span>
                  </div>
                  <span className="sidebar-group-count">{savedChats.length}</span>
                </div>
                {!savedChatsCollapsed && (
                <div className="sidebar-chat-list">
                  {loadingChats ? (
                    <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>Loading...</div>
                  ) : savedChats.length === 0 ? (
                    <div style={{ padding: '12px', color: 'var(--text-faint)', fontSize: '12px' }}>No saved chats</div>
                  ) : (
                    savedChats.map(chat => (
                      <div
                        key={chat.id}
                        className="sidebar-chat-item"
                        onClick={() => handleLoadChat(chat.id)}
                        onMouseEnter={(e) => handleChatMouseEnter(e, chat)}
                        onMouseLeave={handleChatMouseLeave}
                      >
                        <div className="sidebar-chat-title-row">
                          <div className="sidebar-chat-title">{chat.chatTitle}</div>
                          <div className="sidebar-chat-actions">
                            <button
                              className="sidebar-chat-action"
                              onClick={(e) => { e.stopPropagation(); handleRenameChat(chat.id, prompt('New title:', chat.chatTitle) || chat.chatTitle); }}
                              title="Rename"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                <path d="m15 5 4 4" />
                              </svg>
                            </button>
                            <button
                              className="sidebar-chat-action delete"
                              onClick={(e) => { e.stopPropagation(); handleDeleteChat(chat.id); }}
                              title="Delete"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18" />
                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="sidebar-chat-meta">
                          {new Date(chat.lastAccessedAt).toLocaleDateString()} · {chat.messageCount} messages
                        </div>
                        {chat.personaName && (
                          <div className="sidebar-chat-persona-row">
                            <img
                              className="sidebar-chat-persona-icon"
                              src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${chat.personaId}?t=${iconCacheBuster}`}
                              alt=""
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <span className="sidebar-chat-persona-name">{chat.personaName}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                )}
              </div>
            </div>

            <div className="sidebar-footer">
              <div className="sidebar-user-info">
                <div className="sidebar-user-avatar">
                  {(user?.email || 'U').charAt(0).toUpperCase()}
                </div>
                <span className="sidebar-user-email">{user?.email || 'User'}</span>
                <button
                  className="sidebar-logout-icon"
                  onClick={handleLogout}
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </div>
              <ThemeToggle className="sidebar-theme-toggle" />
            </div>
          </>
        ) : (
          <div className="sidebar-header collapsed-header">
            <button className="sidebar-panel-toggle" onClick={() => setSidebarOpen(true)} title="Open sidebar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Chat hover tooltip portal */}
      {hoveredChat && createPortal(
        <div
          className="sidebar-chat-tooltip visible"
          style={{
            top: hoveredChat.top,
            left: hoveredChat.left,
            ...(theme === 'dark' ? {
              background: '#1e1e2e',
              border: '1px solid #333',
              color: '#ccc',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            } : {}),
          }}
        >
          {hoveredChat.summary}
        </div>,
        document.body
      )}

      {/* Main Content */}
      <div className="executive-search-container">
        {/* Client Logo - Top right */}
        {organizationLogo && (
          <div className="ec-watermark-logo">
            <img
              src={organizationLogo}
              alt={organizationSettings?.whitelabelProductName || 'Client logo'}
              className="ec-watermark-img"
            />
          </div>
        )}

        {/* Top Left - Whitelabel Logos */}
        {(whitelabelPrimaryLogo || whitelabelSecondaryLogo) && (
          <div className="whitelabel-logos">
            {whitelabelPrimaryLogo && (
              <img 
                src={whitelabelPrimaryLogo} 
                alt="Primary Logo" 
                className="whitelabel-logo primary"
              />
            )}
            {whitelabelSecondaryLogo && (
              <img 
                src={whitelabelSecondaryLogo} 
                alt="Secondary Logo" 
                className="whitelabel-logo secondary"
              />
            )}
          </div>
        )}

        {/* Top Right - User Info & Persona Selector */}
        <div className="ec-top-right-stats">
          {/* Chat History Button */}
          <button
            className="chat-history-button"
            onClick={handleOpenChatHistory}
            title="Saved Conversations"
          >
            <span className="chat-history-icon">📚</span>
            <span className="chat-history-count-badge">{chatCount}/10</span>
          </button>
          
          {/* Save Chat Button - Only show when messages exist */}
          {messages.length > 0 && (
            <button
              className="save-chat-button"
              onClick={handleOpenSaveChatModal}
              disabled={isStreaming}
              title="Save this conversation"
            >
              <span>💾 Save Chat</span>
            </button>
          )}

          {(allPersonas.length > 0 || availablePersonas.length > 0) && (
            <button
              className="persona-selector-button"
              onClick={() => setShowPersonaModal(true)}
            >
              {selectedPersonaId && (
                <div className="persona-icon-small">
                  <img
                    key={selectedPersonaId}
                    src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${selectedPersonaId}?t=${iconCacheBuster}`}
                    alt="Persona"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              <span className="persona-name">
                {(allPersonas.length > 0 ? allPersonas : availablePersonas).find(p => p.id === selectedPersonaId)?.name || 'Select Persona'}
              </span>
              {allPersonas.length > 1 && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px', opacity: 0.6 }}>
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}
          <div className="ec-stat">
            {user?.email || 'User'}
          </div>
        </div>

        {/* Context Usage Indicator - Right Side */}
        {messages.length > 0 && (
          <div 
            className="context-usage-indicator-right"
            title={`Conversation: ${contextTokens.toLocaleString()} / ${CONVERSATION_TOKEN_BUDGET.toLocaleString()} tokens (${Math.round((contextTokens / CONVERSATION_TOKEN_BUDGET) * 100)}%)`}
          >
            <svg className="context-progress-ring" width="72" height="72">
              <circle
                className="context-progress-ring-circle-bg"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="6"
                fill="transparent"
                r="30"
                cx="36"
                cy="36"
              />
              <circle
                className="context-progress-ring-circle"
                stroke={contextTokens > CONVERSATION_TOKEN_BUDGET ? '#ff9800' : contextTokens > CONVERSATION_TOKEN_BUDGET * 0.75 ? '#ffeb3b' : '#4caf50'}
                strokeWidth="6"
                fill="transparent"
                r="30"
                cx="36"
                cy="36"
                style={{
                  strokeDasharray: `${2 * Math.PI * 30}`,
                  strokeDashoffset: `${2 * Math.PI * 30 * (1 - Math.min(contextTokens / CONVERSATION_TOKEN_BUDGET, 1))}`,
                  transform: 'rotate(-90deg)',
                  transformOrigin: '50% 50%',
                  transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease'
                }}
              />
            </svg>
            <span className="context-percentage-large">
              {Math.round((contextTokens / CONVERSATION_TOKEN_BUDGET) * 100)}%
            </span>
          </div>
        )}

        {user?.organization_id === 51 && (
          <MicrodebRevenueDashboard />
        )}

        {/* Show centered welcome OR messages list */}
        {messages.length === 0 ? (
          /* Initial State - Centered Input */
          <div className="ec-conversation-input">
            <img
              src={theme === 'dark' ? luminaLogoDark : '/LuminaAilogoPNG.png'}
              alt="Lumina AI"
              className="ec-input-logo"
            />
            <p className="ec-input-tagline">Your AI-powered executive assistant</p>
            {/* Rental Dashboard - Only shown for org_id 37 */}
            {user?.organization_id === 37 && (
              <RentalDashboard />
            )}

            {error && (
              <div className="ec-input-error">
                <span className="ec-error-icon">⚠️</span>
                <span className="ec-error-text">{error}</span>
                <button className="ec-error-dismiss" onClick={() => setError(null)}>✕</button>
              </div>
            )}

            <div className="ec-input-with-suggestions">
            <div className="ec-input-container">
              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.pdf,text/csv,application/csv,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              <div className="ec-input-wrapper">
                <textarea
                  ref={inputRef}
                  className="ec-msg-input"
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={placeholderTexts[currentPlaceholderIndex]}
                  disabled={isStreaming || uploadingFiles}
                  rows={1}
                />
                <div className="ec-input-actions">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      className="ec-icon-btn ec-attach-icon"
                      onClick={handleAttachmentClick}
                      disabled={isStreaming || uploadingFiles}
                      title="Attach CSV or PDF files"
                    >
                      {uploadingFiles ? (
                        <div className="ec-send-spinner" />
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      className={`ec-icon-btn ec-voice-icon ${isRecording ? 'recording' : ''}`}
                      onClick={toggleVoiceRecording}
                      disabled={isStreaming || uploadingFiles}
                      title={isRecording ? 'Stop recording' : 'Start voice input'}
                    >
                      {isRecording ? (
                        <div className="ec-recording-indicator">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/>
                          </svg>
                        </div>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                    <button
                      className="ec-icon-btn ec-send-icon"
                      onClick={() => handleSendMessage()}
                      disabled={(!inputMessage.trim() && attachedFiles.length === 0) || isStreaming || uploadingFiles}
                      title="Send message"
                    >
                      {isStreaming ? (
                        <div className="ec-send-spinner" />
                      ) : (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                          <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Attached Files Display - Below Input */}
            {attachedFiles.length > 0 && (
              <div className="attached-files-list">
                {attachedFiles.map((file, index) => (
                  <div key={index} className="attached-file-pill">
                    <span className="pill-filename">{file.name}</span>
                    <button
                      className="pill-remove"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isStreaming || uploadingFiles}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="ec-welcome-suggestions">
              {placeholderTexts.map((text, idx) => (
                <button
                  key={idx}
                  className="ec-suggestion-bubble"
                  onClick={() => { setInputMessage(text); inputRef.current?.focus(); }}
                  disabled={isStreaming}
                >
                  {text}
                </button>
              ))}
            </div>
            </div>{/* close ec-input-with-suggestions */}
          </div>
        ) : (
          /* Chat State - Messages + Input at bottom */
          <>
            <div className="ec-conversation-messages">
              <div className="ec-messages-list">
                {messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`ec-msg ${message.role}`}
                    data-message-id={message.id}
                  >
                    {message.role === 'user' && (
                      <div className="ec-msg-avatar">
                        <span className="ec-avatar-initial">
                          {(user?.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {message.role === 'assistant' && (
                      <div className="ec-msg-avatar">
                        <img
                          src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${selectedPersonaId}?t=${iconCacheBuster}`}
                          alt="AI Assistant"
                          className="ec-avatar-img"
                          onError={(e) => {
                            // If icon not found, show default
                            (e.target as HTMLImageElement).src = theme === 'dark' ? luminaLogoDark : '/LuminaAilogoPNG.png';
                          }}
                        />
                      </div>
                    )}
                    <div className="ec-msg-content">
                      {message.role === 'user' ? (
                        <div className="ec-msg-text">
                          {message.content}
                        </div>
                      ) : (
                        <>
                          {message.isWaitingForResponse ? (
                            <div className="ec-msg-text">
                              <div className="ec-loading-content">
                                <div className="ec-typing-indicator">
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </div>
                                <span>Analyzing your question...</span>
                              </div>
                            </div>
                          ) : message.events && message.events.length > 0 ? (
                            renderTimeline(message.events, message.id)
                          ) : (
                            renderAIContent(message.content || 'Thinking...')
                          )}
                          {!message.isWaitingForResponse && !message.isStreaming && (
                            <>
                              <div className="ec-msg-actions">
                                <span className="ec-msg-actions-label">Export as</span>
                                <button
                                  className="export-word-button"
                                  onClick={() => handleExportToWord(message)}
                                  disabled={exportingMessageId === message.id}
                                  title="Export to Word"
                                >
                                  {exportingMessageId === message.id ? (
                                    <>
                                      <span className="export-spinner"></span>
                                      <span>Exporting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                      </svg>
                                      Word
                                    </>
                                  )}
                                </button>
                                
                                <button
                                  className="export-powerpoint-button"
                                  onClick={() => handleExportToPowerPoint(message)}
                                  disabled={exportingPowerPointMessageId === message.id}
                                  title="Export to PowerPoint"
                                >
                                  {exportingPowerPointMessageId === message.id ? (
                                    <>
                                      <span className="export-spinner"></span>
                                      <span>{exportProgress || 'Creating slides...'}</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                      </svg>
                                      PowerPoint
                                    </>
                                  )}
                                </button>
                                
                                <button
                                  className="export-excel-button"
                                  onClick={() => handleExportToExcel(message)}
                                  disabled={
                                    exportingExcelMessageId === message.id ||
                                    !hasExportableTabularData(extractFinalReportContent(message))
                                  }
                                  title={
                                    hasExportableTabularData(extractFinalReportContent(message))
                                      ? 'Export to Excel'
                                      : 'No tables or KPI cards in this response to export'
                                  }
                                >
                                  {exportingExcelMessageId === message.id ? (
                                    <>
                                      <span className="export-spinner"></span>
                                      <span>Exporting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <line x1="3" y1="9" x2="21" y2="9" />
                                        <line x1="3" y1="15" x2="21" y2="15" />
                                        <line x1="9" y1="3" x2="9" y2="21" />
                                        <line x1="15" y1="3" x2="15" y2="21" />
                                      </svg>
                                      Excel
                                    </>
                                  )}
                                </button>
                                
                                <button
                                  className="export-pdf-button"
                                  onClick={() => handleExportToPDF(message)}
                                  disabled={exportingPDFMessageId === message.id}
                                  title="Export to PDF"
                                >
                                  {exportingPDFMessageId === message.id ? (
                                    <>
                                      <span className="export-spinner"></span>
                                      <span>Exporting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="12" y1="18" x2="12" y2="12" />
                                        <polyline points="9 15 12 12 15 15" />
                                      </svg>
                                      PDF
                                    </>
                                  )}
                                </button>
                              </div>
                              
                              {/* Recommended Questions */}
                              {message.recommendedQuestions && message.recommendedQuestions.length > 0 && (
                                <div className="recommended-questions">
                                  <div className="recommended-header">
                                    <span className="recommended-icon">💡</span>
                                    <span className="recommended-label">Suggested follow-ups:</span>
                                  </div>
                                  <div className="recommended-list">
                                    {message.recommendedQuestions.map((question, idx) => (
                                      <button
                                        key={idx}
                                        className="recommended-question-btn"
                                        onClick={() => {
                                          setInputMessage(question);
                                          inputRef.current?.focus();
                                        }}
                                        disabled={isStreaming}
                                        title="Click to fill this question in the input"
                                      >
                                        {question}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      <div className="ec-msg-time">{formatTime(message.timestamp)}</div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="ec-conversation-input has-messages">
              {error && (
                <div className="ec-input-error">
                  <span className="ec-error-icon">⚠️</span>
                  <span className="ec-error-text">{error}</span>
                  <button className="ec-error-dismiss" onClick={() => setError(null)}>✕</button>
                </div>
              )}

              <div className="ec-input-container">
                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.pdf,text/csv,application/csv,application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                <div className="ec-input-wrapper">
                  <textarea
                    ref={inputRef}
                    className="ec-msg-input"
                    value={inputMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask a follow-up..."
                    disabled={isStreaming || uploadingFiles}
                    rows={1}
                  />
                  <div className="ec-input-actions">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        className="ec-icon-btn ec-attach-icon"
                        onClick={handleAttachmentClick}
                        disabled={isStreaming || uploadingFiles}
                        title="Attach CSV or PDF files"
                      >
                        {uploadingFiles ? (
                          <div className="ec-send-spinner" />
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                      {messages.length > 0 && (
                        <div
                          className="context-ring-small"
                          title={`Conversation: ${contextTokens.toLocaleString()} / ${CONVERSATION_TOKEN_BUDGET.toLocaleString()} tokens (${Math.round((contextTokens / CONVERSATION_TOKEN_BUDGET) * 100)}%)`}
                        >
                          <svg width="36" height="36" viewBox="0 0 28 28">
                            <circle stroke="var(--border-strong)" strokeWidth="3" fill="transparent" r="11" cx="14" cy="14" />
                            <circle
                              stroke={contextTokens > CONVERSATION_TOKEN_BUDGET ? '#ff9800' : contextTokens > CONVERSATION_TOKEN_BUDGET * 0.75 ? '#ffeb3b' : 'var(--accent)'}
                              strokeWidth="3" fill="transparent" r="11" cx="14" cy="14" strokeLinecap="round"
                              style={{
                                strokeDasharray: `${2 * Math.PI * 11}`,
                                strokeDashoffset: `${2 * Math.PI * 11 * (1 - Math.min(contextTokens / CONVERSATION_TOKEN_BUDGET, 1))}`,
                                transform: 'rotate(-90deg)', transformOrigin: '50% 50%',
                                transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease',
                              }}
                            />
                          </svg>
                          <span className="context-ring-pct">{Math.round((contextTokens / CONVERSATION_TOKEN_BUDGET) * 100)}%</span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        className={`ec-icon-btn ec-voice-icon ${isRecording ? 'recording' : ''}`}
                        onClick={toggleVoiceRecording}
                        disabled={isStreaming || uploadingFiles}
                        title={isRecording ? 'Stop recording' : 'Voice input'}
                      >
                        {isRecording ? (
                          <div className="ec-recording-indicator">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/>
                            </svg>
                          </div>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                      <button
                        className="ec-icon-btn ec-send-icon"
                        onClick={() => handleSendMessage()}
                        disabled={(!inputMessage.trim() && attachedFiles.length === 0) || isStreaming || uploadingFiles}
                        title="Send message"
                      >
                        {isStreaming ? (
                          <div className="ec-send-spinner" />
                        ) : (
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  className="new-conversation-button-external"
                  onClick={handleNewConversation}
                  disabled={isStreaming || messages.length === 0}
                  title="Start a new conversation"
                >
                  🔄
                </button>
              </div>

              {/* Attached Files Display - Below Input */}
              {attachedFiles.length > 0 && (
                <div className="attached-files-list">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="attached-file-pill">
                      <span className="pill-filename">{file.name}</span>
                      <button
                        className="pill-remove"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isStreaming || uploadingFiles}
                        title="Remove file"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Bottom Link - always visible */}
        <div className="bottom-link">
          <button className="dashboard-link" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Persona Selection Modal */}
      {showPersonaModal && (
        <div className="persona-modal-overlay" onClick={() => setShowPersonaModal(false)}>
          <div className="persona-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="persona-modal-header">
              <h2>Choose Your AI Persona</h2>
              <button className="persona-modal-close" onClick={() => setShowPersonaModal(false)}>
                ✕
              </button>
            </div>
            <div className="persona-modal-body">
              <div className="persona-grid">
                {allPersonas
                  .filter(persona => persona.visibility !== 'hidden')
                  .map(persona => {
                    const isAvailable = persona.is_available;
                    const isInactive = persona.visibility === 'inactive';
                    const isSelected = persona.id === selectedPersonaId;
                    const canSelect = isAvailable && !isInactive;

                    return (
                      <div
                        key={persona.id}
                        className={`persona-card ${isSelected ? 'selected' : ''} ${!canSelect ? 'unavailable' : ''} ${isInactive ? 'inactive' : ''}`}
                        onClick={() => {
                          if (canSelect) {
                            setSelectedPersonaId(persona.id);
                            setShowPersonaModal(false);
                          }
                        }}
                      >
                        <div className="persona-card-icon">
                          <img
                            src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${persona.id}?t=${iconCacheBuster}`}
                            alt={persona.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = theme === 'dark' ? luminaLogoDark : '/LuminaAilogoPNG.png';
                            }}
                          />
                        </div>
                        <div className="persona-card-info">
                          <h3>{persona.name}</h3>
                          {persona.description && (
                            <p className="persona-card-description">{persona.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="persona-selected-badge">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Active
                          </div>
                        )}
                        {isInactive && (
                          <div className="persona-inactive-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Coming Soon
                          </div>
                        )}
                        {!isAvailable && !isInactive && (
                          <div className="persona-locked-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            Locked
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Chat Modal */}
      <SaveChatModal
        isOpen={showSaveChatModal}
        onClose={() => setShowSaveChatModal(false)}
        onSave={handleSaveChat}
        initialTitle={generateAutoTitle()}
        messageCount={messages.length}
        totalTokens={conversationTokens}
        personaName={availablePersonas.find(p => p.id === selectedPersonaId)?.name || null}
        currentChatCount={chatCount}
      />

      {/* Chat History Panel */}
      <ChatHistoryPanel
        isOpen={showChatHistoryPanel}
        onClose={() => setShowChatHistoryPanel(false)}
        chats={savedChats}
        onLoadChat={handleLoadChat}
        onDeleteChat={handleDeleteChat}
        onRenameChat={handleRenameChat}
        onNewChat={handleNewConversation}
        isLoading={loadingChats}
      />
    </div>
  );
};

export default ExecutiveChatPage;

